import { useEffect } from 'react'
import { useOrganization, useOrganizationList } from '@clerk/clerk-react'

/** Clerk defaults to page size 10; a user with more memberships could have their preferred org sit unfetched on page 2. */
const MEMBERSHIP_LIST_PAGE_SIZE = 100

export interface EnsureActiveOrgProps {
  children: React.ReactNode
  /** Slug to prefer when the user belongs to multiple orgs; defaults to `'loop3'`. */
  preferredOrgSlug?: string
}

/**
 * Auto-selects an active Clerk org on sign-in so users skip Clerk's org
 * picker. Prefers `preferredOrgSlug` (default `'loop3'`), else the first
 * membership. No-ops if an org is already active or there are no memberships.
 */
export function EnsureActiveOrg({ children, preferredOrgSlug = 'loop3' }: EnsureActiveOrgProps) {
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const {
    userMemberships,
    setActive,
    isLoaded: listLoaded,
  } = useOrganizationList({
    userMemberships: { pageSize: MEMBERSHIP_LIST_PAGE_SIZE },
  })

  useEffect(() => {
    if (!orgLoaded || !listLoaded) return
    if (organization) return
    const memberships = userMemberships?.data ?? []
    if (memberships.length === 0) return
    const preferred = memberships.find((m) => m.organization.slug === preferredOrgSlug)
    const target = preferred ?? memberships[0]
    void setActive({ organization: target.organization.id })
  }, [orgLoaded, listLoaded, organization, userMemberships, setActive, preferredOrgSlug])

  return <>{children}</>
}
